import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';
import type { StatusDefinition, UserAction } from '@/config/work-items/types';
import {
  getStaticFinanceStatuses,
  getStaticDataGridVisibleStatuses,
  getStaticAdminSidebarVisibleStatuses,
  getStaticAllowedTransitions,
  getStaticUserActions,
  isStaticSystemStatus,
  isStaticTerminalStatus,
  getStaticStatusColor,
  getStaticStatusLabel,
  isStaticFinanceItemType,
} from '@/config/work-items/financeStatusDefinitions';

/**
 * Check if the WorkItemTypeConfigRegistry is available and initialized.
 * On the client side, the registry cannot be initialized because it relies
 * on Node.js fs and js-yaml modules. In that case, we fall back to the
 * static finance status definitions (financeStatusDefinitions.ts) which
 * mirror the same data from types.yaml.
 */
function isRegistryAvailable(): boolean {
  try {
    const registry = getWorkItemTypeRegistry();
    return registry.isInitialized();
  } catch {
    return false;
  }
}

export function getFinanceStatuses(itemType: string): StatusDefinition[] {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    if (!configLoader.isFinanceType(itemType)) return [];
    return configLoader.getStatusesForItemType(itemType);
  }
  if (!isStaticFinanceItemType(itemType)) return [];
  return getStaticFinanceStatuses(itemType);
}

export function getFinanceDataGridStatuses(itemType: string): StatusDefinition[] {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    if (!configLoader.isFinanceType(itemType)) return [];
    return configLoader.getDataGridVisibleStatuses(itemType);
  }
  if (!isStaticFinanceItemType(itemType)) return [];
  return getStaticDataGridVisibleStatuses(itemType);
}

export function getFinanceAdminSidebarStatuses(itemType: string): StatusDefinition[] {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    if (!configLoader.isFinanceType(itemType)) return [];
    return configLoader.getAdminSidebarVisibleStatuses(itemType);
  }
  if (!isStaticFinanceItemType(itemType)) return [];
  return getStaticAdminSidebarVisibleStatuses(itemType);
}

export function getFinanceAllowedTransitions(itemType: string, currentStatus: string): string[] {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    return configLoader.getAllowedTransitions(itemType, currentStatus);
  }
  return getStaticAllowedTransitions(itemType, currentStatus);
}

export function getFinanceUserActions(itemType: string, currentStatus: string): UserAction[] {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    return configLoader.getUserActions(itemType, currentStatus);
  }
  return getStaticUserActions(itemType, currentStatus);
}

export function isFinanceSystemManagedStatus(status: string, itemType: string): boolean {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    return configLoader.isSystemStatus(itemType, status);
  }
  return isStaticSystemStatus(itemType, status);
}

export function isFinanceTerminalStatus(status: string, itemType: string): boolean {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    return configLoader.isTerminalStatus(itemType, status);
  }
  return isStaticTerminalStatus(itemType, status);
}

// Sync-conflict copies keep status='conflict_review' under item_type
// 'conflict', but surfaces like DocumentViewModal resolve the badge against
// the ORIGINAL doc type — fall back to the conflict type's statuses so the
// badge shows "Conflict Review" instead of raw status text.
export function getFinanceStatusColor(status: string, itemType: string): string {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    return configLoader.getStatusColor(itemType, status)
      || configLoader.getStatusColor('conflict', status)
      || 'gray';
  }
  return getStaticStatusColor(itemType, status)
    || getStaticStatusColor('conflict', status)
    || 'gray';
}

export function getFinanceStatusLabel(status: string, itemType: string): string {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    return configLoader.getStatusLabel(itemType, status)
      || configLoader.getStatusLabel('conflict', status)
      || status;
  }
  return getStaticStatusLabel(itemType, status)
    || getStaticStatusLabel('conflict', status)
    || status;
}

export function isFinanceItemType(itemType: string): boolean {
  if (isRegistryAvailable()) {
    const configLoader = getWorkItemTypeRegistry();
    return configLoader.isFinanceType(itemType);
  }
  return isStaticFinanceItemType(itemType);
}

export interface FinanceStatusTab {
  value: string;
  label: string;
  color?: string;
}

export function getFinanceStatusTabs(itemType: string): FinanceStatusTab[] {
  const statuses = getFinanceStatuses(itemType);
  if (statuses.length === 0) return [];
  return statuses.map(s => ({
    value: s.key,
    label: s.label,
    color: s.color,
  }));
}
